import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { type JobResponse } from '@taxassist/api-client';
import { UserDisplay } from '../common/UserDisplay';
import { getJobEtaDisplay } from '../../utils/jobUtils';
import { isValidUuid } from '../../utils/formatting';

interface OpsJobActionsProps {
  job: JobResponse;
  isEditingEta: boolean;
  etaValue: string;
  onAssignmentClick: () => void;
  onEtaEditClick: () => void;
  onEtaSave: () => void;
  onEtaCancel: () => void;
  onEtaValueChange: (value: string) => void;
  isEtaUpdatePending?: boolean;
  // Engagement PID props (admin only)
  isEditingEngagementPid?: boolean;
  engagementPidValue?: string;
  onEngagementPidEditClick?: () => void;
  onEngagementPidSave?: () => void;
  onEngagementPidCancel?: () => void;
  onEngagementPidValueChange?: (value: string) => void;
  isEngagementPidUpdatePending?: boolean;
  showEngagementPidEdit?: boolean;
}

export function OpsJobActions({
  job,
  isEditingEta,
  etaValue,
  onAssignmentClick,
  onEtaEditClick,
  onEtaSave,
  onEtaCancel,
  onEtaValueChange,
  isEtaUpdatePending = false,
  isEditingEngagementPid,
  engagementPidValue,
  onEngagementPidEditClick,
  onEngagementPidSave,
  onEngagementPidCancel,
  onEngagementPidValueChange,
  isEngagementPidUpdatePending,
  showEngagementPidEdit,
}: OpsJobActionsProps) {
  return (
    <Box sx={{ 
      display: 'flex',
      gap: 2,
      flexWrap: 'wrap',
    }}>
      {/* Assignment */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        flex: '1 1 auto',
        minWidth: 200,
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Assigned to:
        </Typography>
        <UserDisplay
          userId={job.assignedReviewer}
          fallback="Unassigned"
          variant="body2"
        />
        <IconButton
          size="small"
          onClick={onAssignmentClick}
          sx={{ 
            opacity: 0.7,
            '&:hover': { opacity: 1 }
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ETA */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        flex: '1 1 auto',
        minWidth: 200,
      }}>
        {isEditingEta ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              ETA:
            </Typography>
            <TextField
              type="datetime-local"
              value={etaValue}
              onChange={(e) => onEtaValueChange(e.target.value)}
              size="small"
              sx={{ width: 180 }}
            />
            <IconButton
              size="small"
              onClick={onEtaSave}
              disabled={isEtaUpdatePending}
              color="primary"
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onEtaCancel}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              ETA:
            </Typography>
            <Typography variant="body2">
              {getJobEtaDisplay(job)}
            </Typography>
            <IconButton
              size="small"
              onClick={onEtaEditClick}
              sx={{ 
                opacity: 0.7,
                '&:hover': { opacity: 1 }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>

      {/* Engagement PID - Admin only */}
      {showEngagementPidEdit && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flex: '1 1 auto',
          minWidth: 200,
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Engagement ID:
          </Typography>
          {isEditingEngagementPid ? (
            <>
              <TextField
                size="small"
                value={engagementPidValue}
                onChange={(e) => onEngagementPidValueChange?.(e.target.value)}
                placeholder="Enter UUID"
                sx={{ minWidth: 280 }}
                disabled={isEngagementPidUpdatePending}
                error={!!engagementPidValue?.trim() && !isValidUuid(engagementPidValue.trim())}
                helperText={engagementPidValue?.trim() && !isValidUuid(engagementPidValue.trim()) ? 'Must be a valid UUID' : ''}
              />
              <IconButton
                size="small"
                onClick={onEngagementPidSave}
                disabled={!engagementPidValue?.trim() || !isValidUuid(engagementPidValue.trim()) || isEngagementPidUpdatePending}
                color="primary"
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={onEngagementPidCancel}
                disabled={isEngagementPidUpdatePending}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              <Typography variant="body2">
                {job.engagementPid || 'Not set'}
              </Typography>
              <IconButton
                size="small"
                onClick={onEngagementPidEditClick}
                sx={{
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      )}

    </Box>
  );
}
